import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import { Profile } from './model/profile/Profile';
import { getToken } from '../../utils/AdalConfig';
import { ProfileService } from '../../utils/ServiceLocator';
import { AccessAddCommand, AccessRemoveCommand, ClaimAddCommand, ClaimRemoveCommand, RoleChangeCommand, VobAssignmentAddCommand, VobAssignmentRemoveCommand, VobAssignmentUpdateCommand, VobAssignmentBulkImportResponse, UserWorkloadRequest, UserWorkloadResponse, UserAssignedCasesRequest, UserAssignedCasesResponse } from './commands';
import { Claims } from './model/Claims';
import { Roles } from './model/Roles';
import { ProfileAttrSaveCommand } from './commands/ProfileAttrSaveCommand';
import { EntraUser } from './model/entraUser/EntraUser';
import { AssignmentType } from './model/AssignmentType';
import { ProcessType } from './model/ProcessType';

const baseQueryWithAuth = fetchBaseQuery({
    baseUrl: ProfileService,
    prepareHeaders:(headers, { getState }) => {
        const token = getToken();
        headers.set('Authorization', 'Bearer ' + token);
        return headers;
    }
});

const customBaseQuery: typeof baseQueryWithAuth = async (args, api, extraOptions) => {
    // If args is a string (simple GET), or if it's an object with a body that is not FormData, set JSON content-type
    const argsObj = args as any;
    if (typeof args === 'object' && argsObj.body && !(argsObj.body instanceof FormData)) {
        args = {
            ...argsObj,
            headers: {
                ...((argsObj.headers as Record<string, string>) || {}),
                'content-type': 'application/json'
            }
        };
    }
    return baseQueryWithAuth(args, api, extraOptions);
};

export const profileApi = createApi({
    reducerPath:'profileApi',
    baseQuery: customBaseQuery,
    tagTypes:['myProfile','roles','claims','profileLookup','userProfile','allProfiles','jobTitles','userSearch','assignmentTypes','processTypes'],
    endpoints:(builder) =>({
        myProfile:builder.query<Profile,void>({
            query:() =>'myprofile',
            providesTags:['myProfile']
        }),
        profileLookup:builder.query<Profile[],string>({
            query:(term)=>'profileLookup?SearchTerm='+term,
            providesTags:['profileLookup']
        }),
        userProfile:builder.query<Profile,string>({
            query:(id) => 'userProfile?request=' + id,
            providesTags:['userProfile']
        }),
        allProfiles:builder.query<Profile[],void>({
            query:()=>'allProfiles',
            providesTags:['allProfiles']
        }),
        roles:builder.query<Roles[],void>({
            query:()=>'allRoles',
            providesTags:['roles']
        }),
        claims:builder.query<Claims[],void>({
            query:()=>'allClaims',
            providesTags:['claims']
        }),
        allAssignmentTypes:builder.query<AssignmentType[],void>({
            query:()=>'AllAssignmentTypes',
            providesTags:['assignmentTypes']
        }),
        allProcessTypes:builder.query<ProcessType[],void>({
            query:()=>'AllProcessTypes',
            providesTags:['processTypes']
        }),
        roleChange:builder.mutation<void, RoleChangeCommand>({
            query:(body) => {
                return {url:'RoleChange', method:'POST', body:body,}
            },
            invalidatesTags:['myProfile','userProfile']
        }),
        addAccess:builder.mutation<void, AccessAddCommand>({
            query:(body) =>{
                return {url:'AddAccess',method:'POST',body:body,}
            },
            invalidatesTags:['myProfile','userProfile']
        }),
        removeAccess:builder.mutation<void,AccessRemoveCommand>({
            query:(body) =>{
                return {url:'RemoveAccess',method:'POST',body:body,}
            },
            invalidatesTags:['myProfile','userProfile']
        }),
        addClaim:builder.mutation<void,ClaimAddCommand>({
            query:(body)=>{
                return {url:'ClaimAdd',method:'POST',body:body,}
            },
            invalidatesTags:['myProfile','userProfile']
        }),
        removeClaim:builder.mutation<void,ClaimRemoveCommand>({
            query:(body) =>{
                return {url:'ClaimRemove',method:'POST',body:body,}
            },
            invalidatesTags:['myProfile','userProfile']
        }),
        profileAttributeSave:builder.mutation<Profile,ProfileAttrSaveCommand>({
            query:(body) =>{
                return {url:'profileAttributesSave',method:'POST',body:body,}
            },
            invalidatesTags:['myProfile','userProfile']

        }),
        jobTitles:builder.query<string[],void>({
            query:()=>'EntraUsers/JobTitles',
            providesTags:['jobTitles']
        }),
        userSearch:builder.query<EntraUser[], string>({
            query:(term) => '/EntraUsers/Search?SearchTerm=' + term,
            providesTags:['userSearch']
        }),
        addVobAssignment:builder.mutation<Profile, VobAssignmentAddCommand>({
            query:(body) => {
                return {url:'VobAssignment/BulkAdd', method:'POST', body:body}
            },
            invalidatesTags:['myProfile','userProfile'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedProfile } = await queryFulfilled;
                    dispatch(profileApi.util.updateQueryData('allProfiles', undefined, (draft) => {
                        const index = draft.findIndex(p => p.upn === updatedProfile.upn);
                        if (index !== -1) {
                            draft[index] = updatedProfile;
                        }
                    }));
                } catch {}
            }
        }),
        removeVobAssignment:builder.mutation<Profile, VobAssignmentRemoveCommand>({
            query:(body) => {
                return {url:'VobAssignment/Remove', method:'DELETE', body:body}
            },
            invalidatesTags:['myProfile','userProfile'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedProfile } = await queryFulfilled;
                    dispatch(profileApi.util.updateQueryData('allProfiles', undefined, (draft) => {
                        const index = draft.findIndex(p => p.upn === updatedProfile.upn);
                        if (index !== -1) {
                            draft[index] = updatedProfile;
                        }
                    }));
                } catch {}
            }
        }),
        updateVobAssignment:builder.mutation<Profile, VobAssignmentUpdateCommand>({
            query:(body) => {
                return {url:'VobAssignment/Update', method:'PUT', body:body}
            },
            invalidatesTags:['myProfile','userProfile'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedProfile } = await queryFulfilled;
                    dispatch(profileApi.util.updateQueryData('allProfiles', undefined, (draft) => {
                        const index = draft.findIndex(p => p.upn === updatedProfile.upn);
                        if (index !== -1) {
                            draft[index] = updatedProfile;
                        }
                    }));
                } catch {}
            }
        }),
        bulkImportVobAssignments:builder.mutation<VobAssignmentBulkImportResponse, FormData>({
            query:(formData) => {
                return {
                    url:'VobAssignment/CsvImport',
                    method:'POST',
                    body: formData
                }
            },
            invalidatesTags:['myProfile','userProfile','allProfiles']
        }),
        userWorkload:builder.mutation<UserWorkloadResponse, UserWorkloadRequest>({
            query:(body) => {
                return {
                    url:'UserWorkload',
                    method:'POST',
                    body: body
                }
            }
        }),
        userAssignedCases:builder.mutation<UserAssignedCasesResponse, UserAssignedCasesRequest>({
            query:(body) => {
                return {
                    url:'UserAssignedCases',
                    method:'POST',
                    body: body
                }
            }
        })
    })
})

export const {useMyProfileQuery, useProfileLookupQuery, useRolesQuery, useClaimsQuery,
    useRoleChangeMutation,useUserProfileQuery, useAddAccessMutation,
    useRemoveAccessMutation, useAddClaimMutation, useRemoveClaimMutation,
    useAllProfilesQuery, useProfileAttributeSaveMutation, useJobTitlesQuery, useUserSearchQuery,
    useAddVobAssignmentMutation, useRemoveVobAssignmentMutation, useUpdateVobAssignmentMutation, useBulkImportVobAssignmentsMutation,
    useAllAssignmentTypesQuery, useAllProcessTypesQuery, useUserWorkloadMutation, useUserAssignedCasesMutation} = profileApi;
